import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Menu, X, Sun, Moon, ChevronDown } from 'lucide-react';
import { Logo } from './Logo';
import { useTheme } from '../hooks/useTheme';

const SERVICE_SUBITEMS = [
  {
    label: 'Tender Bidding Solutions',
    href: '#tender-bidding',
    desc: 'Govt, PSU & private tender wins',
  },
  {
    label: 'Digital Growth Services',
    href: '#digital-growth',
    desc: 'SEO, paid, content & performance',
  },
  {
    label: 'Website Development',
    href: '#website-development',
    desc: 'Fast, SEO-ready, conversion-first',
  },
  {
    label: 'Customized Business Softwares',
    href: '#custom-software',
    desc: 'ERP, CRM & workflow automation',
  },
];

const NAV_ITEMS = [
  { label: 'Home', href: '#home' },
  { label: 'Why Us', href: '#why-corporates' },
  { label: 'Services', href: '#services', dropdown: true },
  { label: 'Methodology', href: '#why' },
  { label: 'Case Studies', href: '#testimonials' },
  { label: 'FAQ', href: '#faq' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const scheduleCloseDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setServicesOpen(false), 150);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-[#070B16]/80 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a
          href="#home"
          onClick={(e) => handleNavClick(e, '#home')}
          className="flex items-center gap-3"
          data-testid="nav-logo-link"
        >
          <Logo size={40} />
          <div className="hidden sm:block leading-tight">
            <div className="text-white font-bold text-base tracking-tight">Arvanta Solutions</div>
            <div className="text-[11px] text-slate-400">Tender Bidding & Digital Growth</div>
          </div>
        </a>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-1 px-2 py-2 rounded-full glass-card">
          {NAV_ITEMS.map((item) =>
            item.dropdown ? (
              <div
                key={item.href}
                className="relative"
                onMouseEnter={openDropdown}
                onMouseLeave={scheduleCloseDropdown}
              >
                <a
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  aria-haspopup="menu"
                  aria-expanded={servicesOpen}
                  data-testid="nav-services-trigger"
                  className="px-5 py-2 text-sm font-medium text-slate-200 hover:text-white rounded-full hover:bg-white/5 transition-colors inline-flex items-center gap-1.5"
                >
                  {item.label}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      servicesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </a>

                {servicesOpen && (
                  <div
                    role="menu"
                    aria-label="Services"
                    data-testid="nav-services-dropdown"
                    onMouseEnter={openDropdown}
                    onMouseLeave={scheduleCloseDropdown}
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[360px] rounded-2xl glass-card border border-white/10 shadow-2xl shadow-black/40 overflow-hidden animate-fadein"
                  >
                    <div className="px-4 pt-4 pb-2">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
                        What We Offer
                      </div>
                    </div>
                    <ul className="pb-2">
                      {SERVICE_SUBITEMS.map((s) => (
                        <li key={s.href}>
                          <a
                            href={s.href}
                            onClick={(e) => handleNavClick(e, s.href)}
                            data-testid={`nav-services-item-${s.href.replace('#', '')}`}
                            role="menuitem"
                            className="group block px-4 py-3 mx-2 rounded-xl hover:bg-white/5 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-white group-hover:text-amber-300 transition-colors">
                                  {s.label}
                                </div>
                                <div className="text-[11px] text-slate-400 mt-0.5">{s.desc}</div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
                            </div>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="px-5 py-2 text-sm font-medium text-slate-200 hover:text-white rounded-full hover:bg-white/5 transition-colors"
              >
                {item.label}
              </a>
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={toggle}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full btn-ghost-dark"
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="hidden sm:inline-flex items-center gap-2 btn-amber rounded-full px-5 py-3 text-sm"
            data-testid="nav-request-briefing-btn"
          >
            Request Briefing
            <ArrowRight className="w-4 h-4" />
          </a>
          <button
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full btn-ghost-dark"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mx-6 mb-4 rounded-2xl glass-card overflow-hidden">
          {NAV_ITEMS.map((item) =>
            item.dropdown ? (
              <div key={item.href} className="border-b border-white/5 last:border-b-0">
                <div className="flex items-stretch">
                  <a
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="flex-1 block px-6 py-4 text-sm font-medium text-slate-200 hover:bg-white/5"
                  >
                    {item.label}
                  </a>
                  <button
                    type="button"
                    aria-label="Toggle services submenu"
                    data-testid="nav-mobile-services-toggle"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    className="px-5 flex items-center justify-center text-slate-300 hover:bg-white/5 border-l border-white/5"
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>
                {mobileServicesOpen && (
                  <div className="bg-white/[0.03] border-t border-white/5">
                    {SERVICE_SUBITEMS.map((s) => (
                      <a
                        key={s.href}
                        href={s.href}
                        onClick={(e) => handleNavClick(e, s.href)}
                        data-testid={`nav-mobile-services-item-${s.href.replace('#', '')}`}
                        className="block px-8 py-3 text-sm text-slate-300 hover:text-white hover:bg-white/5"
                      >
                        <div className="font-medium">{s.label}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{s.desc}</div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="block px-6 py-4 text-sm font-medium text-slate-200 hover:bg-white/5 border-b border-white/5 last:border-b-0"
              >
                {item.label}
              </a>
            ),
          )}
        </div>
      )}
    </header>
  );
};

export default Navbar;
